import type { ImportsCreateImportRequest, ImportsImportUpdateRequest } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { ImportRecord, ImportSignResponse, ImportState } from './entity';
import type { ImportRules, ImportValidateRules } from './rules';
import { uploadToSignedBucket } from './upload';
import { canSkipInvalidGeosetRows } from './check-geoset-file';
import { isEmptyString } from '@/utils/string';

/**
 * Import API wrappers.
 *
 * Thin adapters over the generated `hydraUiApi.imports.*` client. All bodies
 * and responses are `{ import: {...} }` envelopes with snake_case fields and
 * string ids. Callers work with the wire `ImportRecord`; what each field means
 * for a given import type lives in the page/hook layer.
 *
 * These are plain async functions rather than hooks because the two flows that
 * use them want different things. The CSV wizard reads and writes through the
 * cached layer in `queries.ts` / `mutations.ts`; the geofences pages drive the
 * upload imperatively — sign, upload, create, advance, wait — and need to call
 * the endpoints in order without a query key between each step.
 *
 * Creating an import is a POST. Everything after that is a PUT whose
 * `update_type` tells the server which transition to make, and each transition
 * reads a different set of fields off the body — so every write below derives
 * its body from the matching arm of the generated `ImportUpdateRequest` union
 * rather than restating the fields.
 */

/** The import `type` for geoset fence imports (runs on Temporal, not the shard backfill). */
export const GEOSET_IMPORT_TYPE = 'geoset';

/** `merge` upserts by external_id and removes nothing; `replace` makes membership exactly match the file. */
export type GeosetImportMode = 'merge' | 'replace';

/** Update steps that drive the server-side import state machine, in order. */
export type ImportUpdateStep = 'preprocess' | 'validate' | 'import';

/** The `import` object a create POSTs. */
export type ImportCreateBody = ImportsCreateImportRequest['import'];

type ImportUpdateBody<T extends ImportsImportUpdateRequest['update_type']> = Extract<
  ImportsImportUpdateRequest,
  { update_type: T }
>;

/* -------------------------------------------------------------------------- */
/* Upload signature                                                           */
/* -------------------------------------------------------------------------- */

interface SignArgs {
  workspaceId: number;
  name: string;
  size: number;
  type: string;
}

/** POST /imports/sign → response has exactly one of `{ gcs } | { s3 }`. */
export async function signImportUpload({ workspaceId, name, size, type }: SignArgs): Promise<ImportSignResponse> {
  return hydraUiApi.imports.sign({
    params: { path: { environment_id: String(workspaceId) } },
    // `size` is a String(bytes) on the wire.
    body: { name, size: String(size), type },
  });
}

/* -------------------------------------------------------------------------- */
/* Create                                                                     */
/* -------------------------------------------------------------------------- */

export interface CreateImportInput extends ImportCreateBody {
  workspaceId: number;
}

/**
 * POST /imports → a record in `start`, referencing an uploaded object key (or
 * a Google Sheet). The create body is deliberately narrow: the server ignores
 * `validate_rules` and `import_rules` here and answers with both null, so a
 * wizard's rule choices have to be sent again afterwards through
 * {@link updateImportValidateRules} and {@link updateImportRules}.
 *
 * `description`, `object_type_id` and `segment_id` are required by the schema,
 * and an empty string means "none" — which is how a people import with no
 * target segment is created.
 */
export async function createImport({ workspaceId, ...write }: CreateImportInput): Promise<ImportRecord> {
  const res = await hydraUiApi.imports.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: { import: write },
  });
  return requireImport(res.import);
}

interface CreateGeosetImportArgs {
  workspaceId: number;
  path: string;
  filename: string;
  name: string;
  /** Target geoset — must already exist; the server rejects unknown ids. */
  geosetId: string;
  mode: GeosetImportMode;
  /**
   * Opt into partial validation: the server skips invalid rows (recording
   * them in `validate_meta`) instead of failing the whole file. Off by
   * default server-side; a caller that sends it owns showing the skipped
   * rows on the review step before confirming the import (MBL-2044).
   */
  skipInvalidRows?: boolean;
}

/**
 * POST /imports → create a geoset import referencing the uploaded key. Same
 * envelope as a CSV create plus `geoset_id` and `geoset_mode` — the mode is
 * fixed on the record at create time (see {@link GeosetImportMode}), and so is
 * `geoset_skip_invalid_rows`. A geoset file has no columns to map onto a
 * workspace, so the import goes straight to validation with no mapping step.
 */
export async function createGeosetImport({
  workspaceId,
  path,
  filename,
  name,
  geosetId,
  mode,
  skipInvalidRows = false,
}: CreateGeosetImportArgs): Promise<ImportRecord> {
  return createImport({
    workspaceId,
    type: GEOSET_IMPORT_TYPE,
    geoset_id: geosetId,
    geoset_mode: mode,
    geoset_skip_invalid_rows: skipInvalidRows,
    path,
    filename,
    name,
    description: '',
    object_type_id: '',
    segment_id: '',
  });
}

/** Upload a file and create the geoset import record (sign → bucket POST → create). */
export async function uploadAndCreateGeosetImport(args: {
  workspaceId: number;
  geosetId: string;
  file: File;
  name: string;
  mode: GeosetImportMode;
  skipInvalidRows?: boolean;
}): Promise<ImportRecord> {
  const { workspaceId, geosetId, file, name, mode } = args;
  const skipInvalidRows = args.skipInvalidRows === true && (await canSkipInvalidGeosetRows(file));
  const sign = await signImportUpload({
    workspaceId,
    name: file.name,
    size: file.size,
    type: file.type,
  });
  const { key } = await uploadToSignedBucket(file, sign);
  return createGeosetImport({ workspaceId, geosetId, mode, skipInvalidRows, path: key, filename: file.name, name });
}

/* -------------------------------------------------------------------------- */
/* Advancing the state machine                                                */
/* -------------------------------------------------------------------------- */

interface UpdateArgs {
  workspaceId: number;
  id: string;
  step: ImportUpdateStep;
}

/** PUT /imports/:id with an `update_type` that advances the state machine. */
export async function advanceImport({ workspaceId, id, step }: UpdateArgs): Promise<ImportRecord> {
  return updateImport(workspaceId, id, { update_type: step });
}

/**
 * What the reader chose to do with the imported people, on the review step.
 *
 * Naming a segment creates one; naming an id adds them to that manual segment; neither runs the
 * import on its own. The three are the `import_segment` arm of the update, which the server
 * distinguishes from a plain `import` by the update type rather than by which field is filled.
 */
export interface StartImportInput {
  workspaceId: number;
  id: string;
  /** Create a segment of this name and add the imported people to it. */
  segmentName?: string;
  /** Or add them to this existing manual segment. */
  segmentId?: string;
}

/**
 * PUT `update_type: import` → the server begins writing rows.
 *
 * With a segment named, it is `import_segment` instead, which does the same and puts the people
 * it touched into that segment. Only a people import can ask for one — every other type has no
 * profiles to collect — and the caller is what enforces that, since the server would accept it.
 */
export async function startImport({
  workspaceId,
  id,
  segmentName,
  segmentId,
}: StartImportInput): Promise<ImportRecord> {
  const wantsSegment = !isEmptyString(segmentName) || !isEmptyString(segmentId);

  if (!wantsSegment) {
    return updateImport(workspaceId, id, { update_type: 'import' });
  }

  return updateImport(workspaceId, id, {
    update_type: 'import_segment',
    segment_name: segmentName,
    segment_id: segmentId,
  });
}

/* -------------------------------------------------------------------------- */
/* Downloads                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * POST `/imports/download/{original,import}/:id` → a signed URL for the file.
 *
 * Two of the four files an import can hand back are its own — the file as uploaded, and the rows
 * as they would be written. The other two are export artifacts the validator produced, fetched
 * through the exports endpoint instead; `importDownloadRequest` in the imports feature says
 * which is which.
 */
export async function getImportFileUrl(workspaceId: number, id: string, file: 'original' | 'import'): Promise<string> {
  const params = { path: { environment_id: String(workspaceId), id } };
  const response =
    file === 'original'
      ? await hydraUiApi.imports.downloadOriginal({ params })
      : await hydraUiApi.imports.downloadImport({ params });

  return response.url;
}

/* -------------------------------------------------------------------------- */
/* Resetting                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Everything a reset rewrites. The generated arm marks `name` and
 * `object_type_id` optional, but the server writes both unconditionally —
 * omitting them clears the import's name and unsets its object type — so they
 * are required here.
 */
export interface ResetImportInput extends Omit<ImportUpdateBody<'reset'>, 'update_type' | 'name' | 'object_type_id'> {
  workspaceId: number;
  id: string;
  /** `null` for an import with no name of its own; the server stores `""`. */
  name: string | null;
  /** The object type an object import writes to, or `''` for the other types. */
  object_type_id: string;
}

/**
 * PUT `update_type: reset` → back to `start`, with a new file (or sheet) and a
 * cleared error, every rule and meta document dropped and the row counts
 * zeroed. This is how the wizard takes a second file for an import, and the
 * only way out of `failed`.
 *
 * The whole file identity is part of the body, not just the changed part: the
 * server rewrites `type`, `path`, `filename`, `name`, `description` and
 * `object_type_id` from what is sent, so a caller resending only the new path
 * would blank the rest. Sending a `sheet` switches the import to that
 * spreadsheet and clears `path` and `filename`.
 */
export async function resetImport({ workspaceId, id, ...write }: ResetImportInput): Promise<ImportRecord> {
  return updateImport(workspaceId, id, { ...write, update_type: 'reset' });
}

/**
 * PUT `update_type: reset_preprocessed` → back to `preprocessed`, keeping the
 * file and what preprocessing found, and dropping validation. This is the way
 * back to the mapping step from a validated import, so a mapping change can be
 * re-validated.
 */
export async function resetImportToPreprocessed(workspaceId: number, id: string): Promise<ImportRecord> {
  return updateImport(workspaceId, id, { update_type: 'reset_preprocessed' });
}

/* -------------------------------------------------------------------------- */
/* Rules                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * PUT `update_type: validate_rules` → save the column mapping. The document
 * travels as a JSON string inside the body.
 *
 * The server answers with a conflict when the import was reset while the
 * mapping was being edited, rather than applying rules to a file they were not
 * written for.
 */
export async function updateImportValidateRules(
  workspaceId: number,
  id: string,
  rules: ImportValidateRules,
): Promise<ImportRecord> {
  return updateImport(workspaceId, id, { update_type: 'validate_rules', validate_rules: JSON.stringify(rules) });
}

/** PUT `update_type: import_rules` → save how the mapped columns get applied. */
export async function updateImportRules(workspaceId: number, id: string, rules: ImportRules): Promise<ImportRecord> {
  return updateImport(workspaceId, id, { update_type: 'import_rules', import_rules: JSON.stringify(rules) });
}

/* -------------------------------------------------------------------------- */
/* Reads                                                                      */
/* -------------------------------------------------------------------------- */

/** The page size the endpoint serves; the list page paginates against it. */
export const IMPORTS_PER_PAGE = 50;

/**
 * One page of a workspace's imports, newest first, with the unpaged total for
 * the pager. Only people, event, object and relationship imports appear —
 * segment imports are listed from the segment they belong to, and geoset
 * imports from the geofences page.
 */
export interface ImportsPage {
  imports: ImportRecord[];
  total: number;
}

/**
 * The largest page the endpoint will read. It parses the value with Go's `Atoi` and applies it
 * only when that succeeds and the result is above zero; anything it cannot read that way falls
 * back to the first page. Well under int64, so `Atoi` succeeds and the page is simply past the end
 * instead of quietly turning into page 1.
 */
const MAX_IMPORTS_PAGE = Number.MAX_SAFE_INTEGER;

/**
 * The page number to ask for, as a number the endpoint can read: a whole number between 1 and
 * {@link MAX_IMPORTS_PAGE}. A zero-based pager asking for page 0, a hand-edited URL carrying
 * twenty digits, and anything non-finite are all named explicitly rather than left to the
 * endpoint's fallback.
 */
export function clampImportsPage(page: number): number {
  if (!Number.isFinite(page)) {
    return 1;
  }
  return Math.min(Math.max(1, Math.trunc(page)), MAX_IMPORTS_PAGE);
}

/** GET /imports?page= — one page, plus the total the pager needs. */
export async function getImports(workspaceId: number, page: number): Promise<ImportsPage> {
  const res = await hydraUiApi.imports.list({
    params: { path: { environment_id: String(workspaceId) }, query: { page: clampImportsPage(page) } },
  });
  return { imports: res.imports, total: res.meta.pagination.total };
}

/**
 * GET /imports?type=segment&segment_id=&page=1 — the CSV imports into one manual segment, newest
 * first, on the first page (50 rows). The endpoint falls back to that page when none is given; the
 * page is named so the bound does not rest on that fallback. Segment imports never appear in the
 * untyped list above.
 */
export async function getSegmentImports(workspaceId: number, segmentId: string): Promise<ImportRecord[]> {
  const res = await hydraUiApi.imports.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { type: 'segment', segment_id: Number(segmentId), page: clampImportsPage(1) },
    },
  });
  return res.imports;
}

interface GetArgs {
  workspaceId: number;
  id: string;
  signal?: AbortSignal;
}

/** GET /imports/:id — the polled record. */
export async function getImport({ workspaceId, id, signal }: GetArgs): Promise<ImportRecord> {
  const res = await hydraUiApi.imports.get({
    params: { path: { environment_id: String(workspaceId), id } },
    signal,
  });
  return requireImport(res.import);
}

/* -------------------------------------------------------------------------- */
/* Deleting                                                                   */
/* -------------------------------------------------------------------------- */

/** DELETE /imports/:id → abandon an import the wizard never finished. */
export async function deleteImport(workspaceId: number, id: string): Promise<void> {
  await hydraUiApi.imports.delete({
    params: { path: { environment_id: String(workspaceId), id } },
  });
}

/* -------------------------------------------------------------------------- */
/* Waiting for the server                                                     */
/* -------------------------------------------------------------------------- */

/** How often {@link waitForImportState} re-polls the record (ms). */
export const IMPORT_STATE_WAIT_POLL_MS = 1_500;
/** Safety cap on {@link waitForImportState} so a stuck backfill can't hang forever (~90s). */
export const IMPORT_STATE_WAIT_MAX_ATTEMPTS = 60;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poll GET /imports/:id until the record reaches `target`.
 *
 * The `preprocess` and `validate` steps each queue an async backfill
 * server-side and return with the record still in the *-ing state
 * (`preprocessing` / `validating`). The next PUT is only accepted once that
 * backfill lands the resting state (`preprocessed` / `validated`); issuing it
 * early is rejected with `this import cannot be changed` (400). This waits for
 * the resting state before the caller advances the machine.
 *
 * Resolves once the record rests: `reachedTarget: true` with the record at
 * `target`, or `reachedTarget: false` with a terminal `failed`/`canceled`
 * record — the shape forces callers to handle the terminal branch (stop
 * advancing; the poll surfaces the server's error). Throws only when the wait
 * times out.
 */
export interface ImportStateWaitResult {
  reachedTarget: boolean;
  record: ImportRecord;
}

export async function waitForImportState(args: {
  workspaceId: number;
  id: string;
  target: ImportState;
  pollMs?: number;
  maxAttempts?: number;
  /** Injectable for tests; defaults to the real GET /imports/:id. */
  getRecord?: (a: { workspaceId: number; id: string }) => Promise<ImportRecord>;
}): Promise<ImportStateWaitResult> {
  const {
    workspaceId,
    id,
    target,
    pollMs = IMPORT_STATE_WAIT_POLL_MS,
    maxAttempts = IMPORT_STATE_WAIT_MAX_ATTEMPTS,
    getRecord = getImport,
  } = args;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const record = await getRecord({ workspaceId, id });
    if (record.state === target) {
      return { reachedTarget: true, record };
    }
    if (record.state === 'failed' || record.state === 'canceled') {
      return { reachedTarget: false, record };
    }
    await delay(pollMs);
  }
  throw new Error(`Timed out waiting for import ${id} to reach "${target}".`);
}

/* -------------------------------------------------------------------------- */
/* Error report                                                               */
/* -------------------------------------------------------------------------- */

/**
 * POST /exports/:exportId/download → `{ url }` for the per-row error report.
 *
 * The error report is a separate *export* artifact, referenced by the
 * `invalid_export` id in the validated import's `validate_meta` — NOT the
 * import's own `download/import` file (that's the valid-rows preview).
 */
export async function getImportErrorReportUrl({
  workspaceId,
  exportId,
}: {
  workspaceId: number;
  exportId: number;
}): Promise<string> {
  const res = await hydraUiApi.exports.download({
    params: { path: { environment_id: String(workspaceId), id: String(exportId) } },
  });
  return res.url;
}

/* -------------------------------------------------------------------------- */
/* Shared request shape                                                       */
/* -------------------------------------------------------------------------- */

async function updateImport(
  workspaceId: number,
  id: string,
  update: ImportsImportUpdateRequest,
): Promise<ImportRecord> {
  const res = await hydraUiApi.imports.update({
    params: { path: { environment_id: String(workspaceId), id } },
    body: { import: update },
  });
  return requireImport(res.import);
}

function requireImport(record: ImportRecord | undefined): ImportRecord {
  if (record == null) {
    throw new Error('missing import in response');
  }
  return record;
}
